<template>
  <button
    :disabled="isDisabled"
    :class="modifiers"
    type="button"
    class="dd-icon-button"
    @blur="$emit('blur')"
    @focus="$emit('focus')"
    @click="evt => $emit('click', evt)"
  >
    <i :class="'ddi-' + icon" class="ddi" />
  </button>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

export default defineComponent({
  name: 'DdIconButton',

  props: {
    /* circle or rounded */
    type: {
      type: String,
      default: 'circle',
    },

    /* grey, dark-grey, blue, green */
    color: {
      type: String,
      default: 'grey',
    },

    /* small, medium, or large */
    size: {
      type: String,
      default: 'large',
    },

    isDisabled: {
      type: Boolean,
      default: false,
    },

    /* Name of icon -- e.g. 'conversation' for ddi-conversation icon */
    icon: {
      type: String,
      required: true,
    },

    /* Makes the passed in icon red */
    danger: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['blur', 'click', 'focus'],

  computed: {
    modifiers(): Array<string> {
      const baseSelector = 'dd-icon-button--'

      // Colors
      const selectors = [
        baseSelector + this.size,
        baseSelector + this.type,
        baseSelector + this.color,
      ]

      if (this.danger) {
        selectors.push('dd-icon-button--danger')
      }

      if (this.isDisabled) {
        selectors.push('dd-icon-button--disabled')
      }
      return selectors
    },
  },
})
</script>

<style lang="scss">
.dd-icon-button {
  padding: 0;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;

  > .ddi {
    display: flex;
    align-items: center;
    line-height: 0;
  }

  &:focus {
    outline: none;
  }

  &--danger {
    > .ddi {
      color: $color--red;
    }
  }

  /* Colors */
  &--grey {
    color: var(--color-text-secondary);
    background-color: var(--color-background-secondary);
    border: solid 1px darken($color--gray-15, 15%);

    &:hover:enabled {
      filter: brightness(var(--hover-brightness));
    }
  }

  &--dark-grey {
    color: $color--white;
    background-color: var(--color-text-third);
    border: solid 1px darken($color--gray-60, 10%);

    &:hover:enabled {
      filter: brightness(var(--hover-brightness));
    }
  }

  &--blue {
    color: $color--white;
    background-color: $color--blue;
    border: solid 1px darken($color--blue, 5%);

    &:hover:enabled {
      background-color: darken($color--blue, 5%);
    }
  }

  &--green {
    color: $color--white;
    background-color: $color--green;
    border: solid 1px darken($color--green, 5%);

    &:hover:enabled {
      background-color: darken($color--green, 5%);
    }
  }

  &--orange {
    color: $color--white;
    background-color: $color--orange-40;
    border: solid 1px darken($color--orange-40, 5%);

    &:hover:enabled {
      background-color: darken($color--orange-40, 5%);
    }
  }

  &--white {
    color: var(--color-text-secondary);
    background-color: var(--color-background-header);
    border: solid 1px darken($color--gray-15, 15%);

    &:hover:enabled {
      filter: brightness(var(--hover-brightness));
    }
  }

  /* Types */
  &--circle {
    position: relative;
    border-radius: 50%;
  }

  /* Size */
  &--tiny {
    height: 20px;
    width: 20px;
    font-size: 10px;

    &.dd-icon-button--rounded {
      border-radius: 6px;
    }
  }

  &--small {
    height: 24px;
    width: 24px;
    font-size: 12px;

    &.dd-icon-button--rounded {
      border-radius: 9px;
    }
  }

  &--medium {
    height: 32px;
    width: 32px;
    font-size: 16px;

    &.dd-icon-button--rounded {
      border-radius: 12px;
    }
  }

  &--large {
    height: 40px;
    width: 40px;
    font-size: 20px;

    &.dd-icon-button--rounded {
      border-radius: 16px;
    }
  }

  &--disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
}
</style>
